/** The positioning section's figures: the photos' position sources (`map/photoPositioning.ts`) over each acquisition's run summary. */
import type { PhotoPositioning } from '@/api/reports';
import { POSITION_SOURCES, takeoffAccuracyOf, type PositionSource } from '@/map/photoPositioning';
import type { PhotoProperties } from '@/map/photoSource';
import { summarize, type Summary } from './stats';

type Flight = PhotoPositioning['flights'][number];
export type PpkLog = Flight['ppk'][number];
export type Anchoring = NonNullable<Flight['anchoring']>;
export type PositioningFinding = PhotoPositioning['findings'][number];

export interface SourceShare {
  source: PositionSource;
  count: number;
  pct: number;
}

export interface FlightPositioning {
  /** Unique over the report's acquisitions: two acquisitions may each have a flight `001`. */
  key: string;
  flight: string;
  photos: number | null;
  sources: SourceShare[];
  accH: number | null;
  accV: number | null;
  ppk: PpkLog[];
  anchoring: Anchoring | null;
}

export interface PositioningStats {
  /** The photos whose source is known. */
  total: number;
  sources: SourceShare[];
  rtkPct: number;
  ppkPct: number;
  anchoredCount: number;
  accH: Summary | null;
  accV: Summary | null;
  flights: FlightPositioning[];
  findings: PositioningFinding[];
  /** Some acquisition kept no per photo table: the accuracies stand for the RTK photos only. */
  missingPositionsFile: boolean;
  /** The accuracy (horizontal, vertical; m) a run gave its heights from the take-off, when some photo has one. */
  takeoffAccuracy: [number, number] | null;
}

const MEASURED_PPK: PositionSource[] = ['ppk_fixed', 'ppk_float'];
const ANCHORED: PositionSource[] = ['anchored_overlap', 'anchored_takeoff'];
const TAKEOFF: PositionSource[] = ['takeoff_point', 'takeoff_first_photo'];
const NO_FLIGHT = '—';

function shares(photos: PhotoProperties[]): SourceShare[] {
  const counts = new Map<PositionSource, number>();
  for (const ph of photos) if (ph.position_source) counts.set(ph.position_source, (counts.get(ph.position_source) ?? 0) + 1);
  const total = [...counts.values()].reduce((a, b) => a + b, 0);
  return POSITION_SOURCES.filter((s) => counts.has(s)).map((source) => ({ source, count: counts.get(source)!, pct: (100 * counts.get(source)!) / total }));
}

const median = (values: (number | null | undefined)[]) => summarize(values.map((v) => v ?? null))?.median ?? null;

/** One acquisition's flights: the run's flights in its order, then a flight only the photos name. */
function flightsOf(object: string, photos: PhotoProperties[], positioning: PhotoPositioning): FlightPositioning[] {
  const byFlight = new Map<string, PhotoProperties[]>();
  for (const ph of photos) {
    const key = ph.flight ?? NO_FLIGHT;
    const list = byFlight.get(key);
    if (list) list.push(ph);
    else byFlight.set(key, [ph]);
  }
  const names = [...positioning.flights.map((f) => f.name), ...[...byFlight.keys()].filter((n) => !positioning.flights.some((f) => f.name === n)).sort()];
  return names.map((name) => {
    const summary = positioning.flights.find((f) => f.name === name);
    const list = byFlight.get(name) ?? [];
    return {
      key: `${object}/${name}`,
      flight: name,
      photos: list.length || summary?.photo_count || null,
      sources: shares(list),
      accH: median(list.map((ph) => ph.position_acc_h_m)),
      accV: median(list.map((ph) => ph.position_acc_v_m)),
      ppk: summary?.ppk ?? [],
      anchoring: summary?.anchoring ?? null,
    };
  });
}

/** `null` when no acquisition of the report says where its positions come from (a run before that). */
export function positioningStats(groups: { object: string; photos: PhotoProperties[]; positioning: PhotoPositioning | null | undefined }[]): PositioningStats | null {
  const known = groups.filter((g): g is { object: string; photos: PhotoProperties[]; positioning: PhotoPositioning } => !!g.positioning);
  if (!known.length) return null;
  const photos = known.flatMap((g) => g.photos).filter((ph) => !!ph.position_source);
  const sources = shares(photos);
  const pct = (list: PositionSource[]) => sources.filter((s) => list.includes(s.source)).reduce((a, s) => a + s.pct, 0);
  return {
    total: photos.length,
    sources,
    rtkPct: pct(['rtk']),
    ppkPct: pct(MEASURED_PPK),
    anchoredCount: sources.filter((s) => ANCHORED.includes(s.source)).reduce((a, s) => a + s.count, 0),
    accH: summarize(photos.map((ph) => ph.position_acc_h_m ?? null)),
    accV: summarize(photos.map((ph) => ph.position_acc_v_m ?? null)),
    flights: known.flatMap((g) => flightsOf(g.object, g.photos, g.positioning)),
    findings: known.flatMap((g) => g.positioning.findings),
    missingPositionsFile: known.some((g) => !g.positioning.positions_file),
    takeoffAccuracy: sources.some((s) => TAKEOFF.includes(s.source)) ? (known.map((g) => takeoffAccuracyOf(g.positioning)).find((a) => a !== null) ?? null) : null,
  };
}
